/**
 * ContactsStateBar — the imports-list "Contacts by state" metric.
 *
 * Replaces the bare total in the "Contacts" column with a compact tricolor
 * breakdown (exported / skipped / failed / not-yet-exported) so a recruiter can
 * see, at a glance, what an import has actually done so far — and it updates the
 * instant an export settles (the list polls while `export_in_progress`).
 *
 * While an export is in flight the metric shows a "computing…" state (the final
 * counts are still being written by the async worker); the list's poll flips
 * `export_in_progress` to false on completion and the breakdown renders.
 *
 * Parse lifecycle (2026-07-15, per-user design review): a freshly-created
 * import sits at `status="uploaded"`/`"parsing"` with `total_contacts=0`
 * (the row hasn't been parsed by `import_worker` yet) — previously this
 * rendered as a bare, unstyled "0", indistinguishable from "an import that
 * genuinely parsed zero rows". Now shows an explicit "Importing…" state
 * instead, and a "Failed" state with no fabricated count when the parse
 * itself failed (`status="failed"`) — `useImportsList`'s `refetchInterval`
 * already polls while any visible row is `status==="parsing"`, so this
 * self-resolves into the real count once `import_worker` finishes, no
 * extra polling logic needed here.
 *
 * Design-system tokens only. The sole inline style is the dynamic segment width
 * (the allowed runtime-`%` exception, mirroring `ProgressBar`).
 */
import type { ImportSummary } from "@nu-assist/contracts";
import { Spinner } from "@nu-assist/ui";

export interface ContactsStateBarProps {
  row: Pick<
    ImportSummary,
    | "status"
    | "total_contacts"
    | "exported_count"
    | "skipped_count"
    | "failed_count"
    | "export_in_progress"
  >;
}

export function ContactsStateBar({ row }: ContactsStateBarProps) {
  const total = row.total_contacts;
  const exported = row.exported_count;
  const skipped = row.skipped_count;
  const failed = row.failed_count;
  const remaining = Math.max(0, total - exported - skipped - failed);

  // Parse still in flight — nothing to count yet. Checked before
  // `export_in_progress` (mutually exclusive in practice: export can't
  // start before parsing reaches `ready`), so this always wins for a
  // brand-new row.
  if (row.status === "uploaded" || row.status === "parsing") {
    return (
      <span
        data-testid="contacts-state"
        data-parsing="true"
        className="inline-flex items-center gap-1.5 text-body text-text-secondary tabular-nums"
      >
        <Spinner size="sm" label="Importing" />
        <span className="text-caption text-text-muted">Importing…</span>
      </span>
    );
  }

  // The parse itself failed — no contact count was ever produced. No
  // per-row reason exists yet at the parse level (unlike export/enrichment
  // failures, which already carry `export_error`/`enrichment_error`) — flag
  // the failure plainly rather than showing a misleading "0".
  if (row.status === "failed") {
    return (
      <span
        data-testid="contacts-state"
        data-failed="true"
        className="inline-flex items-center gap-1.5 text-body text-error"
      >
        <span>Failed</span>
      </span>
    );
  }

  // Async export in flight — the worker is still writing per-contact terminal
  // states. Show a computing indicator instead of stale/partial counts.
  if (row.export_in_progress) {
    return (
      <span
        data-testid="contacts-state"
        data-computing="true"
        className="inline-flex items-center gap-1.5 text-body text-text-secondary tabular-nums"
      >
        <Spinner size="sm" label="Computing" />
        <span>{total.toLocaleString()}</span>
        <span className="text-caption text-text-muted">computing…</span>
      </span>
    );
  }

  const hasBreakdown = total > 0 && exported + skipped + failed > 0;
  const pct = (n: number) => `${(n / total) * 100}%`;

  return (
    <span data-testid="contacts-state" className="inline-flex flex-col gap-1">
      <span className="text-body text-text tabular-nums">{total.toLocaleString()}</span>
      {hasBreakdown ? (
        <>
          <span
            className="flex h-1.5 w-24 overflow-hidden rounded-pill bg-bg-subtle"
            title={`${exported} exported · ${skipped} skipped · ${failed} failed · ${remaining} not exported`}
          >
            {exported > 0 ? (
              <span className="block h-full bg-success" style={{ width: pct(exported) }} />
            ) : null}
            {skipped > 0 ? (
              <span className="block h-full bg-warning" style={{ width: pct(skipped) }} />
            ) : null}
            {failed > 0 ? (
              <span className="block h-full bg-error" style={{ width: pct(failed) }} />
            ) : null}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 text-micro tabular-nums">
            {exported > 0 ? <span className="text-success">{exported} exported</span> : null}
            {skipped > 0 ? <span className="text-warning">{skipped} skipped</span> : null}
            {failed > 0 ? <span className="text-error">{failed} failed</span> : null}
          </span>
        </>
      ) : null}
    </span>
  );
}
